import { Partners } from '@lockton-apps/shared/interfaces';
import {
  AccaOverClassOfBusinessId,
  ConstructionClassOfBusinessId,
  EnwClassOfBusinessId,
  LsosClassOfBusinessId,
  NorwayClassOfBusinessId,
  RegulatoryClassOfBusinessId,
} from './cob-meta';

export const DEFAULT_PRODUCT_CONFIG_ID = 1;

export enum LsosProductConfigId {
  Default = DEFAULT_PRODUCT_CONFIG_ID,
}

export enum NorwayProductConfigId {
  Default = DEFAULT_PRODUCT_CONFIG_ID,
}

export enum NorwayPIProductConfigId {
  Default = DEFAULT_PRODUCT_CONFIG_ID,
}

export enum AccaProductConfigId {
  Default = 5,
  Legacy = 1,
}

export enum AccaOverProductConfigId {
  InsolvencyPractitioners = DEFAULT_PRODUCT_CONFIG_ID,
  ShortProposalForm,
  LongProposalForm,
  NewStartUpAccountants,
  ACCA_UNDER,
}

export enum PncProductConfigId {
  Default = DEFAULT_PRODUCT_CONFIG_ID,
}

export enum ConstructionProductConfigId {
  ArchitectsAndEngineers = DEFAULT_PRODUCT_CONFIG_ID,
  DesignAndConstruction,
  Retail,
  MidMarketNonTSA,
  MidMarketTSA,
}

export enum RegulatoryProductConfigId {
  FinancialAdvisers = DEFAULT_PRODUCT_CONFIG_ID,
  Intermediaries,
  LloydsBrokers,
}

export enum EnwProductConfigId {
  Endurance = DEFAULT_PRODUCT_CONFIG_ID,
  Travelers,
  HDIUnder25,
  HDIOver25,
  Solicitors,
  QBE,
  MT,
  SolePractitioner,
  Major,
  MajorShort,
  SolicitorsHdi,
  Solicitors2025,
}

export enum PerformanceProductConfigId {
  Default = DEFAULT_PRODUCT_CONFIG_ID,
}

export const COB_PROPS: Record<number, { [key: number]: string[] }> = {
  [Partners.NORWAY]: {
    [NorwayClassOfBusinessId.TravelBusiness]: [
      `${Partners.NORWAY}-${NorwayProductConfigId.Default}`,
    ],
    [NorwayClassOfBusinessId.Office]: [
      `${Partners.NORWAY}-${NorwayProductConfigId.Default}`,
    ],
    [NorwayClassOfBusinessId.OffWorkAccident]: [
      `${Partners.NORWAY}-${NorwayProductConfigId.Default}`,
    ],
    [NorwayClassOfBusinessId.WorkersCompensation]: [
      `${Partners.NORWAY}-${NorwayProductConfigId.Default}`,
    ],
    [NorwayClassOfBusinessId.FamilyAccident]: [
      `${Partners.NORWAY}-${NorwayProductConfigId.Default}`,
    ],
    [NorwayClassOfBusinessId.FamilyTravel]: [
      `${Partners.NORWAY}-${NorwayProductConfigId.Default}`,
    ],
    [NorwayClassOfBusinessId.SickLeave]: [
      `${Partners.NORWAY}-${NorwayProductConfigId.Default}`,
    ],
    [NorwayClassOfBusinessId.GroupLife]: [
      `${Partners.NORWAY}-${NorwayProductConfigId.Default}`,
    ],
    [NorwayClassOfBusinessId.HealthPrivate]: [
      `${Partners.NORWAY}-${NorwayProductConfigId.Default}`,
    ],
    [NorwayClassOfBusinessId.HealthBusiness]: [
      `${Partners.NORWAY}-${NorwayProductConfigId.Default}`,
    ],
    [NorwayClassOfBusinessId.ProfessionalIndemnity]: [
      `${Partners.NORWAY_PI}-${NorwayProductConfigId.Default}`,
    ],
  },
  [Partners.NORWAY_PI]: {
    [NorwayClassOfBusinessId.TravelBusiness]: [
      `${Partners.NORWAY}-${NorwayProductConfigId.Default}`,
    ],
    [NorwayClassOfBusinessId.Office]: [
      `${Partners.NORWAY}-${NorwayProductConfigId.Default}`,
    ],
    [NorwayClassOfBusinessId.OffWorkAccident]: [
      `${Partners.NORWAY}-${NorwayProductConfigId.Default}`,
    ],
    [NorwayClassOfBusinessId.WorkersCompensation]: [
      `${Partners.NORWAY}-${NorwayProductConfigId.Default}`,
    ],
    [NorwayClassOfBusinessId.FamilyAccident]: [
      `${Partners.NORWAY}-${NorwayProductConfigId.Default}`,
    ],
    [NorwayClassOfBusinessId.FamilyTravel]: [
      `${Partners.NORWAY}-${NorwayProductConfigId.Default}`,
    ],
    [NorwayClassOfBusinessId.SickLeave]: [
      `${Partners.NORWAY}-${NorwayProductConfigId.Default}`,
    ],
    [NorwayClassOfBusinessId.GroupLife]: [
      `${Partners.NORWAY}-${NorwayProductConfigId.Default}`,
    ],
    [NorwayClassOfBusinessId.HealthPrivate]: [
      `${Partners.NORWAY}-${NorwayProductConfigId.Default}`,
    ],
    [NorwayClassOfBusinessId.HealthBusiness]: [
      `${Partners.NORWAY}-${NorwayProductConfigId.Default}`,
    ],
    [NorwayClassOfBusinessId.ProfessionalIndemnity]: [
      `${Partners.NORWAY_PI}-${NorwayProductConfigId.Default}`,
    ],
  },
  [Partners.LSOS]: {
    [LsosClassOfBusinessId.LSOS]: [
      `${Partners.LSOS}-${LsosProductConfigId.Default}`,
    ],
  },
  [Partners.CONSTRUCTION]: {
    [ConstructionClassOfBusinessId.ProfessionsArchitects]: [
      `${Partners.CONSTRUCTION}-${ConstructionProductConfigId.ArchitectsAndEngineers}`,
    ],
    [ConstructionClassOfBusinessId.NationalArchitects]: [
      `${Partners.CONSTRUCTION}-${ConstructionProductConfigId.ArchitectsAndEngineers}`,
    ],
    [ConstructionClassOfBusinessId.ProfessionsEngineers]: [
      `${Partners.CONSTRUCTION}-${ConstructionProductConfigId.ArchitectsAndEngineers}`,
    ],
    [ConstructionClassOfBusinessId.NationalEngineers]: [
      `${Partners.CONSTRUCTION}-${ConstructionProductConfigId.ArchitectsAndEngineers}`,
    ],
    [ConstructionClassOfBusinessId.ProfessionsDesignAndConstruction]: [
      `${Partners.CONSTRUCTION}-${ConstructionProductConfigId.DesignAndConstruction}`,
    ],
    [ConstructionClassOfBusinessId.NationalDesignAndConstruction]: [
      `${Partners.CONSTRUCTION}-${ConstructionProductConfigId.DesignAndConstruction}`,
    ],
    [ConstructionClassOfBusinessId.ProfessionsPropertyManagers]: [
      `${Partners.CONSTRUCTION}-${ConstructionProductConfigId.DesignAndConstruction}`,
      `${Partners.CONSTRUCTION}-${ConstructionProductConfigId.Retail}`,
      `${Partners.CONSTRUCTION}-${ConstructionProductConfigId.MidMarketNonTSA}`,
      `${Partners.CONSTRUCTION}-${ConstructionProductConfigId.MidMarketTSA}`,
    ],
    [ConstructionClassOfBusinessId.NationalPropertyManagers]: [
      `${Partners.CONSTRUCTION}-${ConstructionProductConfigId.DesignAndConstruction}`,
      `${Partners.CONSTRUCTION}-${ConstructionProductConfigId.Retail}`,
      `${Partners.CONSTRUCTION}-${ConstructionProductConfigId.MidMarketNonTSA}`,
      `${Partners.CONSTRUCTION}-${ConstructionProductConfigId.MidMarketTSA}`,
    ],
    [ConstructionClassOfBusinessId.ProfessionsSurveyors]: [
      `${Partners.CONSTRUCTION}-${ConstructionProductConfigId.Retail}`,
      `${Partners.CONSTRUCTION}-${ConstructionProductConfigId.MidMarketNonTSA}`,
      `${Partners.CONSTRUCTION}-${ConstructionProductConfigId.MidMarketTSA}`,
    ],
    [ConstructionClassOfBusinessId.NationalSurveyors]: [
      `${Partners.CONSTRUCTION}-${ConstructionProductConfigId.Retail}`,
      `${Partners.CONSTRUCTION}-${ConstructionProductConfigId.MidMarketNonTSA}`,
      `${Partners.CONSTRUCTION}-${ConstructionProductConfigId.MidMarketTSA}`,
    ],
    [ConstructionClassOfBusinessId.ProfessionsEstateAgent]: [
      `${Partners.CONSTRUCTION}-${ConstructionProductConfigId.Retail}`,
      `${Partners.CONSTRUCTION}-${ConstructionProductConfigId.MidMarketNonTSA}`,
      `${Partners.CONSTRUCTION}-${ConstructionProductConfigId.MidMarketTSA}`,
    ],
    [ConstructionClassOfBusinessId.NationalEstateAgent]: [
      `${Partners.CONSTRUCTION}-${ConstructionProductConfigId.Retail}`,
      `${Partners.CONSTRUCTION}-${ConstructionProductConfigId.MidMarketNonTSA}`,
      `${Partners.CONSTRUCTION}-${ConstructionProductConfigId.MidMarketTSA}`,
    ],
    [ConstructionClassOfBusinessId.NationalSurveyorsRics]: [
      `${Partners.CONSTRUCTION}-${ConstructionProductConfigId.Retail}`,
      `${Partners.CONSTRUCTION}-${ConstructionProductConfigId.MidMarketNonTSA}`,
      `${Partners.CONSTRUCTION}-${ConstructionProductConfigId.MidMarketTSA}`,
    ],
  },
  [Partners.ENW]: {
    [EnwClassOfBusinessId.PI]: [
      `${Partners.ENW}-${EnwProductConfigId.Endurance}`,
      // Travelers is the wrong form and is temporary until the correct form is added
      // `${Partners.ENW}-${EnwProductConfigId.Travelers}`,
      `${Partners.ENW}-${EnwProductConfigId.HDIUnder25}`,
      `${Partners.ENW}-${EnwProductConfigId.HDIOver25}`,
      `${Partners.ENW}-${EnwProductConfigId.Solicitors}`,
      `${Partners.ENW}-${EnwProductConfigId.QBE}`,
      `${Partners.ENW}-${EnwProductConfigId.MT}`,
      `${Partners.ENW}-${EnwProductConfigId.SolePractitioner}`,
      `${Partners.ENW}-${EnwProductConfigId.Major}`,
      `${Partners.ENW}-${EnwProductConfigId.MajorShort}`,
      `${Partners.ENW}-${EnwProductConfigId.SolicitorsHdi}`,
      `${Partners.ENW}-${EnwProductConfigId.Solicitors2025}`,
    ],
    [EnwClassOfBusinessId.PI2]: [
      `${Partners.ENW}-${EnwProductConfigId.Endurance}`,
      // Travelers is the wrong form and is temporary until the correct form is added
      // `${Partners.ENW}-${EnwProductConfigId.Travelers}`,
      `${Partners.ENW}-${EnwProductConfigId.HDIUnder25}`,
      `${Partners.ENW}-${EnwProductConfigId.HDIOver25}`,
      `${Partners.ENW}-${EnwProductConfigId.Solicitors}`,
      `${Partners.ENW}-${EnwProductConfigId.QBE}`,
      `${Partners.ENW}-${EnwProductConfigId.MT}`,
      `${Partners.ENW}-${EnwProductConfigId.SolePractitioner}`,
      `${Partners.ENW}-${EnwProductConfigId.Major}`,
      `${Partners.ENW}-${EnwProductConfigId.MajorShort}`,
      `${Partners.ENW}-${EnwProductConfigId.SolicitorsHdi}`,
      `${Partners.ENW}-${EnwProductConfigId.Solicitors2025}`,
    ],
  },
  [Partners.REGULATORY]: {
    [RegulatoryClassOfBusinessId.AccountantsIfa]: [
      `${Partners.REGULATORY}-${RegulatoryProductConfigId.FinancialAdvisers}`,
    ],
    [RegulatoryClassOfBusinessId.RegulatoryFinancialServices]: [
      `${Partners.REGULATORY}-${RegulatoryProductConfigId.FinancialAdvisers}`,
      `${Partners.REGULATORY}-${RegulatoryProductConfigId.Intermediaries}`,
      `${Partners.REGULATORY}-${RegulatoryProductConfigId.LloydsBrokers}`,
    ],
    [RegulatoryClassOfBusinessId.RegulatoryInsuranceBrokers]: [
      `${Partners.REGULATORY}-${RegulatoryProductConfigId.Intermediaries}`,
      `${Partners.REGULATORY}-${RegulatoryProductConfigId.LloydsBrokers}`,
    ],
    [RegulatoryClassOfBusinessId.RegulatoryMortgageAdvisors]: [
      `${Partners.REGULATORY}-${RegulatoryProductConfigId.FinancialAdvisers}`,
      `${Partners.REGULATORY}-${RegulatoryProductConfigId.Intermediaries}`,
      `${Partners.REGULATORY}-${RegulatoryProductConfigId.LloydsBrokers}`,
    ],
    [RegulatoryClassOfBusinessId.RegulatoryLloyds]: [
      `${Partners.REGULATORY}-${RegulatoryProductConfigId.Intermediaries}`,
      `${Partners.REGULATORY}-${RegulatoryProductConfigId.LloydsBrokers}`,
    ],
    [RegulatoryClassOfBusinessId.RegulatoryUnderwriterAgentsAndManagers]: [
      `${Partners.REGULATORY}-${RegulatoryProductConfigId.Intermediaries}`,
      `${Partners.REGULATORY}-${RegulatoryProductConfigId.LloydsBrokers}`,
    ],
  },
  [Partners.ACCA_OVER]: {
    [AccaOverClassOfBusinessId.NationalAccountants]: [
      `${Partners.ACCA_OVER}-${AccaOverProductConfigId.InsolvencyPractitioners}`,
      `${Partners.ACCA_OVER}-${AccaOverProductConfigId.ShortProposalForm}`,
      `${Partners.ACCA_OVER}-${AccaOverProductConfigId.LongProposalForm}`,
      `${Partners.ACCA_OVER}-${AccaOverProductConfigId.NewStartUpAccountants}`,
    ],
    [AccaOverClassOfBusinessId.ProfessionsAccountants]: [
      `${Partners.ACCA_OVER}-${AccaOverProductConfigId.InsolvencyPractitioners}`,
      `${Partners.ACCA_OVER}-${AccaOverProductConfigId.ShortProposalForm}`,
      `${Partners.ACCA_OVER}-${AccaOverProductConfigId.LongProposalForm}`,
      `${Partners.ACCA_OVER}-${AccaOverProductConfigId.NewStartUpAccountants}`,
    ],
    [AccaOverClassOfBusinessId.NationalAccountantsAcca]: [
      `${Partners.ACCA_OVER}-${AccaOverProductConfigId.InsolvencyPractitioners}`,
      `${Partners.ACCA_OVER}-${AccaOverProductConfigId.ShortProposalForm}`,
      `${Partners.ACCA_OVER}-${AccaOverProductConfigId.LongProposalForm}`,
      `${Partners.ACCA_OVER}-${AccaOverProductConfigId.NewStartUpAccountants}`,
      `${Partners.ACCA}-${AccaOverProductConfigId.ACCA_UNDER}`,
    ],
  },
  [Partners.ACCA]: {
    [AccaOverClassOfBusinessId.NationalAccountants]: [
      `${Partners.ACCA_OVER}-${AccaOverProductConfigId.LongProposalForm}`,
      `${Partners.ACCA_OVER}-${AccaOverProductConfigId.ShortProposalForm}`,
      `${Partners.ACCA_OVER}-${AccaOverProductConfigId.NewStartUpAccountants}`,
    ],
    [AccaOverClassOfBusinessId.ProfessionsAccountants]: [
      `${Partners.ACCA_OVER}-${AccaOverProductConfigId.LongProposalForm}`,
      `${Partners.ACCA_OVER}-${AccaOverProductConfigId.ShortProposalForm}`,
      `${Partners.ACCA_OVER}-${AccaOverProductConfigId.NewStartUpAccountants}`,
    ],
    [AccaOverClassOfBusinessId.NationalAccountantsAcca]: [
      `${Partners.ACCA}-${AccaProductConfigId.Default}`,
      `${Partners.ACCA_OVER}-${AccaOverProductConfigId.LongProposalForm}`,
      `${Partners.ACCA_OVER}-${AccaOverProductConfigId.ShortProposalForm}`,
      `${Partners.ACCA_OVER}-${AccaOverProductConfigId.NewStartUpAccountants}`,
      `${Partners.ACCA}-${AccaOverProductConfigId.ACCA_UNDER}`,
    ],
  },
};

export function getPartnerCobProps(partner: Partners) {
  return COB_PROPS[partner];
}
